import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { AccountLedgerResponse, ChartOfAccount } from '../types';

export interface ChartOfAccountsFilters {
  type?: string;
  active?: '' | 'true' | 'false';
  currency?: string;
  system?: '' | 'true' | 'false';
  search?: string;
}

export function useChartOfAccountsList(filters: ChartOfAccountsFilters | string = {}) {
  const normalized = typeof filters === 'string' ? { type: filters } : filters;
  return useQuery({
    queryKey: ['accounts', 'chart-of-accounts', 'list', normalized],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(normalized).forEach(([key, value]) => value && params.set(key, value));
      const { data } = await api.get<ChartOfAccount[]>('/api/accounts/chart-of-accounts', { params });
      return data;
    },
  });
}

export function useAccountLedger(accountId: string | null, filters: { startDate?: string; endDate?: string; page?: number } = {}) {
  return useQuery({
    queryKey: ['accounts', 'chart-of-accounts', accountId, 'ledger', filters],
    queryFn: async () => {
      const { data } = await api.get<AccountLedgerResponse>(`/api/accounts/chart-of-accounts/${accountId}/ledger`, { params: { ...filters, limit: 100 } });
      return data;
    },
    enabled: !!accountId,
  });
}

export function useCreateChartOfAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<ChartOfAccount>) => {
      const { data } = await api.post('/api/accounts/chart-of-accounts', payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'chart-of-accounts'] }),
  });
}

export function useUpdateChartOfAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<ChartOfAccount> & { id: string }) => {
      const { data } = await api.put(`/api/accounts/chart-of-accounts/${id}`, payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'chart-of-accounts'] }),
  });
}

export function useDeleteChartOfAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/api/accounts/chart-of-accounts/${id}`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'chart-of-accounts'] }),
  });
}

export interface ImportRbmChartResult {
  message: string;
  total: number;
  inserted: number;
  skipped: number;
  insertedCodes: string[];
  conflicts: { code: string; name: string; existingName: string }[];
}

export function useImportRbmChart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<ImportRbmChartResult>('/api/accounts/chart-of-accounts/import-rbm');
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'chart-of-accounts'] }),
  });
}

export interface IndustryTemplateInfo {
  key: string;
  label: string;
  description: string;
  addsAccounts: number;
}

export function useIndustryTemplates() {
  return useQuery({
    queryKey: ['accounts', 'chart-of-accounts', 'industry-templates'],
    queryFn: async () => {
      const { data } = await api.get<IndustryTemplateInfo[]>('/api/accounts/chart-of-accounts/industry-templates');
      return data;
    },
  });
}

export function useApplyIndustryTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (industry: string) => {
      const { data } = await api.post<{ message: string; inserted: number; skipped: number; industry: string }>(
        '/api/accounts/chart-of-accounts/apply-template',
        { industry }
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'chart-of-accounts'] }),
  });
}
